import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Tooltip } from '../components/ui/Tooltip';
import { Textarea } from '../components/ui/Textarea';
import { GenerateActivityWizardModal } from '../components/operations/GenerateActivityWizardModal';
import { ActivityProgressBadge } from '../components/operations/ActivityProgressBadge';
import { ActivityDetailModal } from '../components/operations/ActivityDetailModal';
import { operativeActivitiesStore, getProgress, OperativeActivity } from '../services/operativeActivities';
import { Plus, Filter, Download, Eye, Edit, Search, FileText, Euro, Calendar, LayoutGrid, List as ListIcon, AlertTriangle, CheckCircle2, X, Save, Trash2, AlertCircle, ClipboardList } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';
import { PermissionGate } from '../components/PermissionGate';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { mockData } from '../services/mockData';
import { motion, AnimatePresence } from 'framer-motion';
const initialContractsData = mockData.getContracts();
const columnsConfig = [{
  key: 'number',
  label: 'Codice'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'product',
  label: 'Prodotto'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'amount',
  label: 'Importo'
}, {
  key: 'expiry',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
// Updated Kanban columns as per specs
const kanbanColumns = [{
  id: 'da_proporre',
  title: 'Da proporre',
  color: 'bg-slate-50 border-slate-200'
}, {
  id: 'in_valutazione',
  title: 'In valutazione',
  color: 'bg-blue-50 border-blue-200'
}, {
  id: 'da_sollecitare',
  title: 'Da sollecitare',
  color: 'bg-yellow-50 border-yellow-200'
}, {
  id: 'accettato',
  title: 'Accettato',
  color: 'bg-green-50 border-green-200'
}, {
  id: 'non_accettato',
  title: 'Non accettato',
  color: 'bg-red-50 border-red-200'
}, {
  id: 'revocato',
  title: 'Revocato',
  color: 'bg-orange-50 border-orange-200'
}];
const statusLabels: Record<string, string> = {
  da_proporre: 'Da proporre',
  in_valutazione: 'In valutazione',
  da_sollecitare: 'Da sollecitare',
  accettato: 'Accettato',
  non_accettato: 'Non accettato',
  revocato: 'Revocato',
  // Legacy mappings
  proposal: 'Da proporre',
  pending: 'In valutazione',
  urgent: 'Da sollecitare',
  accepted: 'Accettato',
  rejected: 'Non accettato',
  revoked: 'Revocato'
};
export function ContractsPage() {
  const navigate = useNavigate();
  const {
    canCreate,
    canEdit
  } = useRBAC();
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('kanban');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [kanbanData, setKanbanData] = useState(initialContractsData);
  const [draggedCard, setDraggedCard] = useState<any>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isGenerateActivityOpen, setIsGenerateActivityOpen] = useState(false);
  const [editFormStatus, setEditFormStatus] = useState('');
  const [editFormAcceptanceDate, setEditFormAcceptanceDate] = useState('');
  const [editFormRejectionDate, setEditFormRejectionDate] = useState('');
  const [editFormRejectionReason, setEditFormRejectionReason] = useState('');
  const [editFormRevokeDate, setEditFormRevokeDate] = useState('');
  const [editFormRevokeReason, setEditFormRevokeReason] = useState('');
  const [selectedOperativeActivity, setSelectedOperativeActivity] = useState<OperativeActivity | null>(null);
  const [isActivityDetailOpen, setIsActivityDetailOpen] = useState(false);
  const [, setActivityRefresh] = useState(0);
  const [contractActivities, setContractActivities] = useState<OperativeActivity[]>([]);
  const [selectedActivityForDetail, setSelectedActivityForDetail] = useState<OperativeActivity | null>(null);
  const [showActivityDetailModal, setShowActivityDetailModal] = useState(false);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(kanbanData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['number', 'client']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    // Load activities for this contract
    const activities = operativeActivitiesStore.getByContract(item.number);
    setContractActivities(activities);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setEditFormStatus(item.status || 'da_proporre');
    setEditFormAcceptanceDate(item.acceptanceDate || '');
    setEditFormRejectionDate(item.rejectionDate || '');
    setEditFormRejectionReason(item.rejectionReason || '');
    setEditFormRevokeDate(item.revokeDate || '');
    setEditFormRevokeReason(item.revokeReason || '');
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setEditFormStatus('da_proporre');
    setEditFormAcceptanceDate('');
    setEditFormRejectionDate('');
    setEditFormRejectionReason('');
    setEditFormRevokeDate('');
    setEditFormRevokeReason('');
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
    }, 1500);
  };
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (draggedCard && draggedCard.status !== targetColumnId) {
      setKanbanData(prev => prev.map(item => item.id === draggedCard.id ? {
        ...item,
        status: targetColumnId
      } : item));
    }
    setDraggedCard(null);
  };
  const getCardsByColumn = (columnId: string) => {
    const statusMap: Record<string, string[]> = {
      da_proporre: ['da_proporre', 'proposal'],
      in_valutazione: ['in_valutazione', 'pending'],
      da_sollecitare: ['da_sollecitare', 'urgent'],
      accettato: ['accettato', 'accepted'],
      non_accettato: ['non_accettato', 'rejected'],
      revocato: ['revocato', 'revoked']
    };
    const validStatuses = statusMap[columnId] || [columnId];
    return searchResult.filter(c => validStatuses.includes(c.status));
  };
  const handleActivityCreated = (activityId: number) => {
    // Refresh activities list
    if (selectedItem) {
      const activities = operativeActivitiesStore.getByContract(selectedItem.number);
      setContractActivities(activities);
    }
  };
  const handleViewActivityDetail = (activity: OperativeActivity) => {
    setSelectedActivityForDetail(activity);
    setShowActivityDetailModal(true);
  };
  const handleStepComplete = (activityId: string, stepIndex: number) => {
    operativeActivitiesStore.completeStep(activityId, stepIndex);
    const updated = operativeActivitiesStore.getById(activityId);
    if (updated) setSelectedOperativeActivity({
      ...updated
    });
    setActivityRefresh(r => r + 1);
  };
  const handleEditActivityFromContract = (activity: OperativeActivity) => {
    setIsActivityDetailOpen(false);
    // Open the edit form for the activity
    // Find the corresponding contract to provide context
    const matchingContract = kanbanData.find(c => c.number === activity.contractNumber);
    if (matchingContract) {
      setSelectedItem(matchingContract);
      setEditFormStatus(matchingContract.status || 'da_proporre');
      setEditFormAcceptanceDate(matchingContract.acceptanceDate || '');
      setEditFormRejectionDate(matchingContract.rejectionDate || '');
      setEditFormRejectionReason(matchingContract.rejectionReason || '');
      setEditFormRevokeDate(matchingContract.revokeDate || '');
      setEditFormRevokeReason(matchingContract.revokeReason || '');
      setIsModalOpen(true);
    }
  };
  return <Layout data-id="element-2819">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2820">
        <div data-id="element-2821">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2822">
            Offerte e contratti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2823">
            Gestione del ciclo di vita dei contratti e delle offerte.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2824">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-2825">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2826">
              <LayoutGrid className="h-4 w-4 inline-block mr-1.5" data-id="element-2827" /> Kanban
            </button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2828">
              <ListIcon className="h-4 w-4 inline-block mr-1.5" data-id="element-2829" /> Lista
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2831" />} data-id="element-2830">
            Esporta
          </Button>
          <PermissionGate module="contracts" action="create" data-id="element-2832">
            <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2834" />} data-id="element-2833">
              Nuova offerta
            </Button>
          </PermissionGate>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-2835">
        <div className="p-4 flex flex-col gap-4" data-id="element-2836">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2837">
            <div className="flex-1" data-id="element-2838">
              <Input placeholder="Cerca contratto, cliente..." startContent={<Search className="h-4 w-4" data-id="element-2840" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2839" />
            </div>
            <div className="flex gap-2" data-id="element-2841">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2843" />} data-id="element-2842">
                Filtri{' '}
                {Object.keys(filters).filter(k => filters[k]).length > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2844">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              {viewMode === 'list' && <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="contracts" lockedColumns={['number']} data-id="element-2845" />}
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2846">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'da_proporre',
            label: 'Da proporre'
          }, {
            value: 'in_valutazione',
            label: 'In valutazione'
          }, {
            value: 'da_sollecitare',
            label: 'Da sollecitare'
          }, {
            value: 'accettato',
            label: 'Accettato'
          }, {
            value: 'non_accettato',
            label: 'Non accettato'
          }, {
            value: 'revocato',
            label: 'Revocato'
          }]} data-id="element-2847" />
              <div className="col-span-full flex justify-end" data-id="element-2848">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2850" />} data-id="element-2849">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      {viewMode === 'list' ? <Card noPadding className="overflow-hidden" data-id="element-2851">
          <div className="overflow-x-auto" data-id="element-2852">
            <table className="w-full text-left border-collapse table-auto" data-id="element-2853">
              <thead data-id="element-2854">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2855">
                  {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-4 py-4 ${col.key === 'actions' ? 'text-left pr-6' : ''}`} data-id="element-2856">
                        {col.label}
                      </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2857">
                {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2858">
                    {visibleColumns.includes('number') && <td className="px-4 py-4 font-mono text-xs text-slate-500" data-id="element-2859">
                        {item.number}
                      </td>}
                    {visibleColumns.includes('client') && <td className="px-4 py-4 text-sm font-medium text-slate-900" data-id="element-2860">
                        {item.client}
                      </td>}
                    {visibleColumns.includes('product') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2861">
                        {item.product}
                      </td>}
                    {visibleColumns.includes('status') && <td className="px-4 py-4" data-id="element-2862">
                        <Badge variant={['accettato', 'accepted'].includes(item.status) ? 'success' : ['in_valutazione', 'pending', 'da_sollecitare', 'urgent'].includes(item.status) ? 'warning' : ['non_accettato', 'rejected', 'revocato', 'revoked'].includes(item.status) ? 'danger' : 'neutral'} data-id="element-2863">
                          {statusLabels[item.status] || item.status}
                        </Badge>
                      </td>}
                    {visibleColumns.includes('amount') && <td className="px-4 py-4 text-sm font-mono text-slate-900" data-id="element-2864">
                        {item.amount}
                      </td>}
                    {visibleColumns.includes('expiry') && <td className="px-4 py-4 text-sm text-slate-600" data-id="element-2865">
                        {item.expiry}
                      </td>}
                    {visibleColumns.includes('actions') && <td className="px-4 py-4 pr-6" data-id="element-2866">
                        <div className="flex items-center gap-1" data-id="element-2867">
                          <Tooltip content="Visualizza contratto" data-id="element-2868">
                            <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-2869">
                              <Eye className="h-4 w-4 text-slate-500" data-id="element-2870" />
                            </Button>
                          </Tooltip>
                          <PermissionGate module="contracts" action="edit" data-id="element-2871">
                            <Tooltip content="Modifica contratto" data-id="element-2872">
                              <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-2873">
                                <Edit className="h-4 w-4 text-seform-orange" data-id="element-2874" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                          <PermissionGate module="contracts" action="delete" data-id="element-2875">
                            <Tooltip content="Elimina contratto" data-id="element-2876">
                              <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-2877">
                                <Trash2 className="h-4 w-4 text-red-500" data-id="element-2878" />
                              </Button>
                            </Tooltip>
                          </PermissionGate>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100" data-id="element-2879">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2880" />
          </div>
        </Card> :
    // Kanban View with fixed scroll
    <div className="overflow-x-auto pb-4" data-id="element-2881">
          <div className="flex gap-4 min-w-max" data-id="element-2882">
            {kanbanColumns.map(column => {
          const cards = getCardsByColumn(column.id);
          return <div key={column.id} className={`w-72 flex-shrink-0 rounded-xl border ${column.color} flex flex-col max-h-[calc(100vh-280px)]`} onDragOver={handleDragOver} onDrop={e => handleDrop(e, column.id)} data-id="element-2883">
                  <div className="p-3 border-b border-slate-200/50 flex items-center justify-between" data-id="element-2884">
                    <h3 className="font-semibold text-slate-700 text-sm" data-id="element-2885">
                      {column.title}
                    </h3>
                    <Badge variant="neutral" size="sm" data-id="element-2886">
                      {cards.length}
                    </Badge>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2" data-id="element-2887">
                    {cards.map(card => <div key={card.id} draggable onDragStart={e => handleDragStart(e, card)} onClick={() => handleView(card)} className={`bg-white rounded-lg p-3 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition-all ${draggedCard?.id === card.id ? 'opacity-50' : ''}`} data-id="element-2888">
                        <div className="flex items-start justify-between mb-2" data-id="element-2889">
                          <span className="text-xs font-mono text-slate-400" data-id="element-2890">
                            {card.number}
                          </span>
                          <div className="w-6 h-6 rounded-full bg-seform-navy/10 flex items-center justify-center text-[10px] font-bold text-seform-navy" data-id="element-2891">
                            AL
                          </div>
                        </div>
                        <h4 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-1" data-id="element-2892">
                          {card.client}
                        </h4>
                        <p className="text-xs text-slate-500 mb-2 line-clamp-1" data-id="element-2893">
                          {card.product}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-2894">
                          <span className="text-sm font-mono font-medium text-seform-navy" data-id="element-2895">
                            {card.amount}
                          </span>
                          <span className="text-xs text-slate-400" data-id="element-2896">
                            {card.expiry}
                          </span>
                        </div>
                      </div>)}
                    {cards.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-2897">
                        Nessun elemento
                      </div>}
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {/* View Modal */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio contratto" size="2xl" data-id="element-2898">
        {selectedItem && <div className="space-y-8" data-id="element-2899">
            <section data-id="element-2900">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2901">
                <FileText className="h-4 w-4" data-id="element-2902" /> Dati generali
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-2903">
                <div data-id="element-2904">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2905">
                    Codice
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2906">
                    {selectedItem.number}
                  </p>
                </div>
                <div data-id="element-2907">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2908">
                    Cliente
                  </span>
                  <p className="font-medium mt-1" data-id="element-2909">{selectedItem.client}</p>
                </div>
                <div data-id="element-2910">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2911">
                    Prodotto
                  </span>
                  <p className="font-medium mt-1" data-id="element-2912">{selectedItem.product}</p>
                </div>
                <div data-id="element-2913">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2914">
                    Tipologia
                  </span>
                  <p className="font-medium mt-1" data-id="element-2915">
                    {selectedItem.tipologia || '-'}
                  </p>
                </div>
                <div data-id="element-2916">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2917">
                    Responsabile sviluppo
                  </span>
                  <p className="font-medium mt-1" data-id="element-2918">
                    {selectedItem.responsabileSviluppo || 'Alessio'}
                  </p>
                </div>
                <div data-id="element-2919">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2920">
                    Provenienza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2921">
                    {selectedItem.provenienza || '-'}
                  </p>
                </div>
                <div data-id="element-2922">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2923">
                    Corrispettivo provenienza
                  </span>
                  <p className="font-mono font-medium mt-1" data-id="element-2924">
                    {selectedItem.corrispettivoProvenienza || '-'}
                  </p>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-2925"></div>

            <section data-id="element-2926">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2927">
                <AlertTriangle className="h-4 w-4" data-id="element-2928" /> Stato e workflow
              </h4>
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-2929">
                <div data-id="element-2930">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2931">
                    Stato attuale
                  </span>
                  <div className="mt-1" data-id="element-2932">
                    <Badge variant={['accettato', 'accepted'].includes(selectedItem.status) ? 'success' : ['in_valutazione', 'pending', 'da_sollecitare', 'urgent'].includes(selectedItem.status) ? 'warning' : ['non_accettato', 'rejected', 'revocato', 'revoked'].includes(selectedItem.status) ? 'danger' : 'neutral'} data-id="element-2933">
                      {statusLabels[selectedItem.status] || selectedItem.status}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-2934">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2935">
                    Data cambio stato
                  </span>
                  <p className="font-medium mt-1" data-id="element-2936">
                    {selectedItem.dataCambioStato || selectedItem.date || '15/03/2025'}
                  </p>
                </div>
              </div>

              {/* Conditional status-specific fields */}
              {['accettato', 'accepted'].includes(selectedItem.status) && <div className="bg-green-50 p-4 rounded-lg border border-green-100" data-id="element-2937">
                  <h4 className="text-sm font-bold text-green-800 uppercase tracking-wider mb-3" data-id="element-2938">
                    Dettagli accettazione
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2939">
                    <div data-id="element-2940">
                      <span className="text-xs font-semibold text-green-600 uppercase" data-id="element-2941">
                        Data di accettazione
                      </span>
                      <p className="font-medium text-green-900" data-id="element-2942">
                        {selectedItem.acceptanceDate || '15/03/2025'}
                      </p>
                    </div>
                  </div>
                </div>}

              {['non_accettato', 'rejected'].includes(selectedItem.status) && <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-2943">
                  <h4 className="text-sm font-bold text-red-800 uppercase tracking-wider mb-3" data-id="element-2944">
                    Dettagli non accettazione
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2945">
                    <div data-id="element-2946">
                      <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-2947">
                        Data non accettazione
                      </span>
                      <p className="font-medium text-red-900" data-id="element-2948">
                        {selectedItem.rejectionDate || '20/02/2025'}
                      </p>
                    </div>
                    <div data-id="element-2949">
                      <span className="text-xs font-semibold text-red-600 uppercase" data-id="element-2950">
                        Motivazione
                      </span>
                      <p className="font-medium text-red-900" data-id="element-2951">
                        {selectedItem.rejectionReason || 'Budget insufficiente'}
                      </p>
                    </div>
                  </div>
                </div>}

              {['revocato', 'revoked'].includes(selectedItem.status) && <div className="bg-orange-50 p-4 rounded-lg border border-orange-100" data-id="element-2952">
                  <h4 className="text-sm font-bold text-orange-800 uppercase tracking-wider mb-3" data-id="element-2953">
                    Dettagli revoca
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2954">
                    <div data-id="element-2955">
                      <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-2956">
                        Data revoca
                      </span>
                      <p className="font-medium text-orange-900" data-id="element-2957">
                        {selectedItem.revokeDate || '10/01/2025'}
                      </p>
                    </div>
                    <div data-id="element-2958">
                      <span className="text-xs font-semibold text-orange-600 uppercase" data-id="element-2959">
                        Motivazione
                      </span>
                      <p className="font-medium text-orange-900" data-id="element-2960">
                        {selectedItem.revokeReason || 'Cambio fornitore'}
                      </p>
                    </div>
                  </div>
                </div>}
            </section>

            <div className="h-px bg-slate-100" data-id="element-2961"></div>

            <section data-id="element-2962">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2963">
                <Euro className="h-4 w-4" data-id="element-2964" /> Dettagli economici
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-2965">
                <div data-id="element-2966">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2967">
                    Importo
                  </span>
                  <p className="font-mono font-bold text-seform-navy mt-1" data-id="element-2968">
                    {selectedItem.amount}
                  </p>
                </div>
                <div data-id="element-2969">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2970">
                    Durata (mesi)
                  </span>
                  <p className="font-medium mt-1" data-id="element-2971">
                    {selectedItem.durata || '12'}
                  </p>
                </div>
                <div data-id="element-2972">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2973">
                    Data offerta
                  </span>
                  <p className="font-medium mt-1" data-id="element-2974">
                    {selectedItem.dataPreventivo || selectedItem.date || '10/01/2025'}
                  </p>
                </div>
                <div data-id="element-2975">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2976">
                    Scadenza
                  </span>
                  <p className="font-medium mt-1" data-id="element-2977">{selectedItem.expiry}</p>
                </div>
                <div data-id="element-2978">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-2979">
                    Tacito rinnovo
                  </span>
                  <p className="font-medium mt-1" data-id="element-2980">
                    {selectedItem.tacitRenewal !== false ? 'Sì' : 'No'}
                  </p>
                </div>
              </div>
            </section>

            <div className="h-px bg-slate-100" data-id="element-2981"></div>

            {/* Attività operative section - NEW */}
            <section data-id="element-2982">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2983">
                <ClipboardList className="h-4 w-4" data-id="element-2984" /> Attività operative
              </h4>
              {(() => {
            const contractActivities = operativeActivitiesStore.getByContract(selectedItem.number);
            if (contractActivities.length === 0) {
              return <div className="text-center py-6 bg-slate-50 rounded-lg border border-slate-100" data-id="element-2985">
                      <ClipboardList className="h-6 w-6 mx-auto mb-2 text-slate-300" data-id="element-2986" />
                      <p className="text-sm text-slate-400" data-id="element-2987">
                        Nessuna attività operativa generata per questo contratto
                      </p>
                    </div>;
            }
            return <div className="space-y-3" data-id="element-2988">
                    {contractActivities.map(act => {
                const prog = getProgress(act);
                return <div key={act.id} onClick={() => {
                  setSelectedOperativeActivity(act);
                  setIsActivityDetailOpen(true);
                }} className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${prog.isOverdue ? 'bg-red-50 border-red-200 hover:bg-red-50/80' : prog.isCompleted ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200 hover:bg-white'}`} data-id="element-2989">
                          <div className="flex items-center justify-between" data-id="element-2990">
                            <div data-id="element-2991">
                              <h5 className="font-semibold text-sm text-slate-900" data-id="element-2992">
                                {act.title}
                              </h5>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2993">
                                Operatore: {act.operator} · Scadenza:{' '}
                                {new Date(act.deadline).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                            <div className="w-40" data-id="element-2994">
                              <ActivityProgressBadge currentStep={prog.currentStep} totalSteps={prog.totalSteps} percentage={prog.percentage} isCompleted={prog.isCompleted} isOverdue={prog.isOverdue} size="sm" data-id="element-2995" />
                            </div>
                          </div>
                        </div>;
              })}
                  </div>;
          })()}
            </section>

            <div className="flex justify-between items-center pt-4 border-t border-slate-100" data-id="element-2996">
              <div data-id="element-2997">
                {['accettato', 'accepted'].includes(selectedItem.status) && <Button variant="flat" color="success" startContent={<ClipboardList className="h-4 w-4" data-id="element-2999" />} onClick={() => {
              setIsViewModalOpen(false);
              navigate('/operations', {
                state: {
                  openWizard: true,
                  contractNumber: selectedItem?.number,
                  clientName: selectedItem?.client
                }
              });
            }} data-id="element-2998">
                    Genera attività operativa
                  </Button>}
              </div>
              <div className="flex gap-3" data-id="element-3000">
                <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-3001">
                  Chiudi
                </Button>
                <PermissionGate module="contracts" action="edit" data-id="element-3002">
                  <Button onClick={() => {
                setIsViewModalOpen(false);
                handleEdit(selectedItem);
              }} startContent={<Edit className="h-4 w-4" data-id="element-3004" />} data-id="element-3003">
                    Modifica
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>}
      </Modal>

      {/* Generate Activity Modal - Updated with props */}
      <GenerateActivityWizardModal isOpen={isGenerateActivityOpen} onClose={() => setIsGenerateActivityOpen(false)} contractNumber={selectedItem?.number} clientName={selectedItem?.client} data-id="element-3005" />

      {/* Activity Detail Modal */}
      <ActivityDetailModal isOpen={isActivityDetailOpen} onClose={() => setIsActivityDetailOpen(false)} activity={selectedOperativeActivity} onStepComplete={handleStepComplete} onEdit={handleEditActivityFromContract} data-id="element-3006" />

      {/* Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica contratto' : 'Nuova offerta'} size="2xl" data-id="element-3007">
        <AnimatePresence mode="wait" data-id="element-3008">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-3009">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3010">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-3011" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3012">
                Contratto salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3013">
              <div className="space-y-8" data-id="element-3014">
                <section data-id="element-3015">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3016">
                    <FileText className="h-4 w-4" data-id="element-3017" /> Dati generali
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3018">
                    <ProgressiveNumberInput type="contract" value={selectedItem?.number} onChange={() => {}} disabled={false} data-id="element-3019" />
                    <SearchableSelect label="Cliente" value={selectedItem?.client || ''} onChange={() => {}} options={[{
                  value: 'Azienda 0 SRL',
                  label: 'Azienda 0 SRL'
                }, {
                  value: 'Studio 1 Associati',
                  label: 'Studio 1 Associati'
                }, {
                  value: 'Impresa 2 SPA',
                  label: 'Impresa 2 SPA'
                }, {
                  value: 'Azienda 3 SRL',
                  label: 'Azienda 3 SRL'
                }]} required data-id="element-3020" />
                    <Select label="Prodotto" defaultValue={selectedItem?.product} options={[{
                  value: 'Consulenza Sicurezza',
                  label: 'Consulenza Sicurezza'
                }, {
                  value: 'HACCP Base',
                  label: 'HACCP Base'
                }, {
                  value: 'Privacy GDPR',
                  label: 'Privacy GDPR'
                }, {
                  value: 'Formazione Lavoratori',
                  label: 'Formazione Lavoratori'
                }, {
                  value: 'DVR',
                  label: 'DVR'
                }]} required data-id="element-3021" />
                    <Select label="Tipologia" defaultValue={selectedItem?.tipologia || ''} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, {
                  value: 'Sicurezza',
                  label: 'Sicurezza'
                }, {
                  value: 'Privacy',
                  label: 'Privacy'
                }, {
                  value: 'HACCP',
                  label: 'HACCP'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} data-id="element-3022" />
                    <Select label="Responsabile sviluppo" options={[{
                  value: 'alessio',
                  label: 'Alessio'
                }, {
                  value: 'gianfranco',
                  label: 'Gian Franco'
                }, {
                  value: 'lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-3023" />
                    <Select label="Commissionato da:" defaultValue={selectedItem?.provenienza || ''} options={[{
                  value: '',
                  label: 'Seleziona...'
                }, {
                  value: 'Commerciale SEF',
                  label: 'Commerciale SEF'
                }, {
                  value: 'Esterni e partner',
                  label: 'Esterni e partner'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} data-id="element-3024" />
                    <Input label="Corrispettivo provenienza (€)" placeholder="Es. 500,00" defaultValue={selectedItem?.corrispettivoProvenienza?.replace('€ ', '') || ''} data-id="element-3025" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-3026"></div>

                <section data-id="element-3027">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3028">
                    <AlertTriangle className="h-4 w-4" data-id="element-3029" /> Stato e workflow
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3030">
                    <Select label="Stato attuale" value={editFormStatus} onChange={e => setEditFormStatus(e.target.value)} options={[{
                  value: 'da_proporre',
                  label: 'Da proporre'
                }, {
                  value: 'in_valutazione',
                  label: 'In valutazione'
                }, {
                  value: 'da_sollecitare',
                  label: 'Da sollecitare'
                }, {
                  value: 'accettato',
                  label: 'Accettato'
                }, {
                  value: 'non_accettato',
                  label: 'Non accettato'
                }, {
                  value: 'revocato',
                  label: 'Revocato'
                }]} required data-id="element-3031" />
                    <Input label="Data dell'offerta" type="date" value={editFormStatus === 'accettato' ? editFormAcceptanceDate : new Date().toISOString().split('T')[0]} onChange={() => {}} data-id="element-3032" />

                    {editFormStatus === 'accettato' && <Input label="Data di accettazione" type="date" value={editFormAcceptanceDate} onChange={e => setEditFormAcceptanceDate(e.target.value)} data-id="element-3033" />}

                    {editFormStatus === 'non_accettato' && <>
                        <Input label="Data non accettazione" type="date" value={editFormRejectionDate} onChange={e => setEditFormRejectionDate(e.target.value)} data-id="element-3034" />
                        <div className="col-span-full" data-id="element-3035">
                          <Input label="Motivazione" placeholder="Inserisci la motivazione..." value={editFormRejectionReason} onChange={e => setEditFormRejectionReason(e.target.value)} data-id="element-3036" />
                        </div>
                      </>}

                    {editFormStatus === 'revocato' && <>
                        <Input label="Data revoca" type="date" value={editFormRevokeDate} onChange={e => setEditFormRevokeDate(e.target.value)} data-id="element-3037" />
                        <div className="col-span-full" data-id="element-3038">
                          <Input label="Motivazione" placeholder="Inserisci la motivazione della revoca..." value={editFormRevokeReason} onChange={e => setEditFormRevokeReason(e.target.value)} data-id="element-3039" />
                        </div>
                      </>}
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-3040"></div>

                <section data-id="element-3041">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-3042">
                    <Euro className="h-4 w-4" data-id="element-3043" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3044">
                    <Input label="Importo (€)" type="number" placeholder="0.00" defaultValue={selectedItem?.amount?.replace('€ ', '')} required data-id="element-3045" />
                    <Input label="Durata (mesi)" type="number" defaultValue="12" required data-id="element-3046" />
                    <Input label="Data offerta" type="date" defaultValue="2025-01-10" data-id="element-3047" />
                    <div className="flex items-center gap-2 pt-6" data-id="element-3048">
                      <input type="checkbox" id="renewal" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" defaultChecked={selectedItem?.tacitRenewal ?? false} data-id="element-3049" />
                      <label htmlFor="renewal" className="text-sm text-slate-700" data-id="element-3050">
                        Tacito rinnovo
                      </label>
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-3051">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-3052">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-3054" />} data-id="element-3053">
                    Salva contratto
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-3055">
        <AnimatePresence mode="wait" data-id="element-3056">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-3057">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-3058">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-3059" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-3060">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-3061">
              <div className="space-y-6" data-id="element-3062">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-3063">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-3064" />
                  <div data-id="element-3065">
                    <p className="text-sm text-red-700 font-medium" data-id="element-3066">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-3067">
                      Il contratto <strong data-id="element-3068">{itemToDelete?.number}</strong> verrà
                      eliminato definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-3069">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-3070">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-3072" />} data-id="element-3071">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}